'use client';

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface BlogPost {
  title: string;
  slug: string;
  date: string;
  category: string;
  excerpt: string;
}

const blogPosts: BlogPost[] = [
  {
    title: 'The Pros and Cons of Free Hosting: Why You Get What You Pay For',
    slug: 'the-pros-and-cons-of-free-hosting-services-why-you-get-what-you-pay-for',
    date: 'December 10, 2024',
    category: 'Web Hosting',
    excerpt: 'Explore the advantages and disadvantages of free hosting services, helping you make an informed decision about your website\'s future.'
  },
  {
    title: '10 Questions to Ask Before Choosing a Hosting Service',
    slug: '10-questions-to-ask-before-choosing-a-hosting-service-for-your-website',
    date: 'May 9, 2023',
    category: 'Web Hosting',
    excerpt: 'Essential questions to ask when selecting a hosting provider, ensuring you make the right choice for your website needs.'
  },
  {
    title: 'Why Security Should Be a Top Priority When Choosing a Hosting Service',
    slug: 'why-security-should-be-a-top-priority-when-choosing-a-hosting-service',
    date: 'November 28, 2024',
    category: 'Security',
    excerpt: 'Learn why cybersecurity is crucial when selecting a hosting provider and what security features to look for.'
  },
  {
    title: 'Maximizing Uptime: How Reliable Hosting Ensures Your Website Stays Online',
    slug: 'maximizing-uptime-how-reliable-hosting-ensures-your-website-stays-online',
    date: 'November 24, 2024',
    category: 'Performance',
    excerpt: 'Discover how reliable hosting services contribute to maximizing your website\'s uptime and why it matters for your online presence.'
  },
  {
    title: 'The Ultimate Guide to Choosing the Perfect Domain Name',
    slug: 'the-ultimate-guide-to-choosing-the-perfect-domain-name',
    date: 'December 5, 2024',
    category: 'Domain Names',
    excerpt: 'Learn the essential tips and best practices for selecting a memorable and effective domain name for your website.'
  },
  {
    title: 'Understanding Website Performance Metrics',
    slug: 'understanding-website-performance-metrics',
    date: 'December 8, 2024',
    category: 'Performance',
    excerpt: 'A comprehensive guide to key performance indicators that affect your website\'s speed, user experience, and search rankings.'
  },
  {
    title: 'Cloud Hosting vs Traditional Hosting: Making the Right Choice',
    slug: 'cloud-hosting-vs-traditional-hosting-making-the-right-choice',
    date: 'December 1, 2024',
    category: 'Web Hosting',
    excerpt: 'Compare the benefits and drawbacks of cloud hosting and traditional hosting solutions to find the best fit for your needs.'
  },
  {
    title: 'From Shared to Dedicated Hosting: Which Service is Right for You and Why',
    slug: 'from-shared-to-dedicated-hosting-which-service-is-right-for-you-and-why',
    date: 'November 15, 2024',
    category: 'Web Hosting',
    excerpt: 'A comprehensive guide to help you choose between shared, VPS, and dedicated hosting based on your specific website needs.'
  },
  {
    title: 'How Our Hosting Service Can Improve Your Website\'s Speed and Performance',
    slug: 'how-our-hosting-service-can-improve-your-websites-speed-and-performance',
    date: 'November 10, 2024',
    category: 'Performance',
    excerpt: 'Discover how 1 Dollar Cloud\'s hosting solutions can significantly enhance your website\'s loading times and overall performance.'
  },
  {
    title: 'The Cheapest Shared Hosting in the Market: A Comprehensive Comparison',
    slug: 'the-cheapest-shared-hosting-in-the-market-a-comprehensive-comparison',
    date: 'November 5, 2024',
    category: 'Web Hosting',
    excerpt: 'An in-depth analysis of affordable shared hosting options, with a focus on finding the best value for your money.'
  }
];

export default function RelatedBlogCarousel({ currentSlug }: { currentSlug: string }) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const relatedPosts = blogPosts.filter(post => post.slug !== currentSlug);

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (carouselRef.current) {
        currentIndex = (currentIndex + 1) % relatedPosts.length;
        const scrollAmount = currentIndex * (carouselRef.current.scrollWidth / relatedPosts.length);
        carouselRef.current.scrollTo({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [relatedPosts.length]);

  return (
    <section className="py-16 bg-slate-900/50 backdrop-blur-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">
          More Similar Articles
        </h2>
        <div
          ref={carouselRef}
          className="overflow-x-hidden whitespace-nowrap scroll-smooth"
        >
          {relatedPosts.map((post, index) => (
            <motion.div
              key={post.slug}
              className="inline-block w-full md:w-1/2 lg:w-1/3 px-4 whitespace-normal"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={`/${post.slug}`}>
                <div className="bg-slate-800/50 rounded-xl p-6 h-full hover:bg-slate-800/70 transition-colors duration-300">
                  <p className="text-slate-400 text-sm mb-2">{post.category}</p>
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">{post.title}</h3>
                  <p className="text-slate-300 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                  <p className="text-slate-400 text-sm">{post.date}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}